import {MarginSummary} from "../../types/PortfolioMarginResult";
import {IsoDateTime} from "../../pseudo-types/DateTypes";
import {OptionType} from "../../orders/models/OrderResponse";
import {OptionStyle} from "../../types/ChainService";

export interface PortfolioSummary {
    currentEtoLedger: number;
    currentEquityLedger: number;
    currentCmaBalance: number;
    portfolioId: number;
    accountName: string;
    accountNumber: string;
    status: string;
    broker: string;
    totalCash: number;
    totalCollateral: number;
    pnl: number;
    clientMargin: number;
    excess: number;
    marginLimit: number | null;
    nlv: number;
    nlvMark: number;
    equityValue: number;
    todayPnl: number;
    todayNlv: number;
    todayMargin: number;
    todayEquityValue: number;
    todayCashBalance: number;
    todayShortageExcess: number;
    weekPnl: number;
    weekNlv: number;
    weekMargin: number;
    weekEquityValue: number;
    weekCashBalance: number;
    hasPositions: boolean;
    hasTraded: boolean;
    portfolioType: PortfolioType;
    scratch: boolean;
}

export enum PortfolioType {
    BROKER = "BROKER",
    LINKED = "LINKED",
    SCRATCH = "SCRATCH",
    SIMULATED = "SIMULATED"
}

export interface PortfolioPositions {
    id: number;
    accountName: string;
    accountNumber: string;
    equityAccountNumber: string;
    updated: string;
    marginLimit: number | null;
    marginMultiplier: number;
    limitMultiplier: number;
    eodEtoLedger: number;
    currentEtoLedger: number;
    eodEquityLedger: number;
    currentEquityLedger: number;
    eodCashMargin: number;
    collateralBalance: number;
    eodCmaBalance: number;
    currentCmaBalance: number;
    reportedShortage: number;
    positions: ReadonlyArray<Position>;
    equityPositions: ReadonlyArray<EquityPosition>;
    eodMarginSummary: MarginSummary;
    historicalSummary: HistoricalSummary[];
    realisedProfit: any[];
    nextEtoExpiry: string;
    nextIndexExpiry: string;
    today: string;
    marginSchedule: null;
    etoBrokerageSchedule: null;
    lepoBrokerageSchedule: null;
    equityBrokerageSchedule: null;
    portfolioType: PortfolioType;
    scratch: boolean;
    linked: boolean;
    linkedUsers: any[];
    updatedParamsAvailable: boolean;
}

export interface Position {
    readonly id: number;
    readonly quantity: number;
    readonly security: string;
    readonly code: string;
    readonly strike: number;
    readonly expiry: IsoDateTime;
    readonly type: OptionType;
    readonly style: OptionStyle;
    readonly marginEffect: number | null;
    readonly delta: number;
    readonly dollarDelta: number;
    readonly vega: number;
    readonly theta: number;
    readonly rho: number;
    readonly gamma: number;
    readonly netDelta: number;
    readonly netDeltaPercent: number;
    readonly netVega: number;
    readonly netTheta: number;
    readonly netRho: number;
    readonly netGamma: number;
    readonly openPrice: number;
    readonly theoPrice: number;
    readonly premiumMargin: number;
    readonly scanRisk: number;
    readonly vol: number;
    readonly netEffect: number | null;
    readonly cvf: number;
    readonly underlyingPrice: number;
    readonly unrealPnL: number;
    readonly todayPnL: number;
    readonly additionalMargin: number;
}

export interface EquityPosition {
    id: number;
    code: string;
    quantity: number;
    averagePrice: number;
    externalRef: null;
}

export interface HistoricalSummary {
    pointInTime: string;
    shortageExcess: number;
    clientMargin: number;
    pnl: number;
    nlv: number;
    cash: number;
    equityValue: number;
}
